// Kratos browser login-flow client for the /login page (RFC-0005 Task 3.1).
//
// Repo convention: plain same-origin `fetch` (the SPA is served from the API's
// own origin, and Kratos is fronted by the API's `/kratos/*` proxy — no CORS,
// no Ory SDK, no axios). This is a one-shot login-flow client and stays on plain
// `fetch`; the SPA's *read* data layer wraps `fetch` with TanStack Query per
// ADR-0007 (`fetch` remains the transport). Non-2xx / network failures map to a
// typed error class so the page can show a retry banner (mirrors lib/sync-api.ts).
//
// The `/login` page uses this to (1) start or re-read a Kratos self-service
// login flow, (2) locate the Google OIDC submit control + csrf token to render
// a real top-level POST form, and (3) forward a `return_to` deep link — but
// only ever as a leading-slash RELATIVE path (open-redirect defense, §3),
// defense-in-depth atop Kratos' own `allowed_return_urls` validation.

/** A single Kratos UI text (flow- or node-level message). */
export interface KratosUiText {
  id?: number;
  text: string;
  /** "info" | "error" | "success" */
  type?: string;
  context?: Record<string, unknown>;
}

/** Attributes of a Kratos UI node (only the fields the /login page reads). */
export interface KratosUiNodeAttributes {
  name?: string;
  /** input type, e.g. "hidden" | "submit". */
  type?: string;
  value?: unknown;
  node_type?: string;
  disabled?: boolean;
}

/** A single node in a Kratos flow's UI (input, anchor, image, text, script). */
export interface KratosUiNode {
  type: string;
  /** "default" | "oidc" | "password" | … */
  group: string;
  attributes: KratosUiNodeAttributes;
  messages?: KratosUiText[];
}

/** The subset of a Kratos self-service login flow the /login page consumes. */
export interface KratosLoginFlow {
  id: string;
  return_to?: string;
  ui: {
    /** Absolute URL (portal origin + /kratos/…) to POST the flow to. */
    action: string;
    /** Usually "POST". */
    method: string;
    nodes: KratosUiNode[];
    messages?: KratosUiText[];
  };
}

/**
 * A network failure or a non-200 from Kratos (through the proxy). Shown by the
 * /login page as a banner with a Retry action — the flow could not be loaded.
 */
export class LoginFlowError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'LoginFlowError';
  }
}

// Whitespace, space, and C0 control chars plus DEL (0x7f) a browser might strip
// or normalize inside a URL, turning a "relative" path into a cross-origin one.
// eslint-disable-next-line no-control-regex
const UNSAFE_CHARS = /[\u0000-\u0020\u007f]/;

/**
 * Sanitize an untrusted `return_to` to a safe, leading-slash relative path.
 *
 * Returns the value only when it is a same-origin relative path (`/projects/x`);
 * returns `undefined` for anything else — absolute/cross-origin URLs
 * (`https://evil.com`), protocol-relative (`//evil.com`), backslash tricks
 * (`/\evil.com`, which some browsers normalize to `//`), or values carrying
 * control characters. This is the client-side open-redirect defense (§3);
 * Kratos independently re-validates against `allowed_return_urls`.
 */
export function sanitizeReturnTo(raw: string | null | undefined): string | undefined {
  if (!raw) return undefined;
  if (UNSAFE_CHARS.test(raw)) return undefined;
  // Must be a relative path anchored at a single leading slash.
  if (raw[0] !== '/') return undefined;
  // Reject protocol-relative ("//host") and backslash-normalized ("/\host").
  if (raw[1] === '/' || raw[1] === '\\') return undefined;
  return raw;
}

async function readFlow(res: Response, what: string): Promise<KratosLoginFlow> {
  if (res.status !== 200) {
    throw new LoginFlowError(
      `Could not start sign-in (${what} returned ${res.status}). Please try again.`,
    );
  }
  try {
    return (await res.json()) as KratosLoginFlow;
  } catch {
    throw new LoginFlowError('Received an unexpected response from the server.');
  }
}

/**
 * Start a new browser login flow. Forwards `returnTo` (sanitized to a relative
 * path) so Kratos redirects there on success. Rejects with LoginFlowError on
 * network failure or a non-200 response.
 */
export async function fetchLoginFlow(returnTo?: string): Promise<KratosLoginFlow> {
  const safe = sanitizeReturnTo(returnTo);
  let path = '/kratos/self-service/login/browser';
  if (safe) path += `?return_to=${encodeURIComponent(safe)}`;

  let res: Response;
  try {
    res = await fetch(path, {
      headers: { Accept: 'application/json' },
      credentials: 'include',
    });
  } catch {
    throw new LoginFlowError('Could not reach the server. Check your connection and try again.');
  }
  return readFlow(res, 'login/browser');
}

/**
 * Re-read an existing flow by id (e.g. after Kratos redirects back to /login
 * with `?flow=<id>` carrying a rejection message such as the out-of-domain
 * error). Rejects with LoginFlowError on network failure or a non-200 response.
 */
export async function fetchExistingFlow(flowId: string): Promise<KratosLoginFlow> {
  const path = `/kratos/self-service/login/flows?id=${encodeURIComponent(flowId)}`;
  let res: Response;
  try {
    res = await fetch(path, {
      headers: { Accept: 'application/json' },
      credentials: 'include',
    });
  } catch {
    throw new LoginFlowError('Could not reach the server. Check your connection and try again.');
  }
  return readFlow(res, 'login/flows');
}

/**
 * Locate the Google OIDC submit control in a flow's UI nodes. Prefers the node
 * whose value is `google`; falls back to the first OIDC submit node.
 */
export function findGoogleOidcNode(flow: KratosLoginFlow): KratosUiNode | undefined {
  const oidcSubmits = flow.ui.nodes.filter(
    (n) =>
      n.group === 'oidc' &&
      n.attributes?.name === 'provider' &&
      n.attributes?.type === 'submit',
  );
  return (
    oidcSubmits.find((n) => String(n.attributes.value).toLowerCase() === 'google') ??
    oidcSubmits[0]
  );
}

/** Extract the hidden `csrf_token` value (required on the submit form). */
export function getCsrfToken(flow: KratosLoginFlow): string | undefined {
  const node = flow.ui.nodes.find((n) => n.attributes?.name === 'csrf_token');
  const value = node?.attributes?.value;
  return typeof value === 'string' ? value : undefined;
}

/** Collect flow-level + node-level messages (for inline error/info rendering). */
export function collectFlowMessages(flow: KratosLoginFlow): KratosUiText[] {
  const messages: KratosUiText[] = [...(flow.ui.messages ?? [])];
  for (const node of flow.ui.nodes) {
    if (node.messages?.length) messages.push(...node.messages);
  }
  return messages;
}
